import React, { useEffect, useMemo } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import FaqBusinessHoursSection from "./FaqBusinessHoursSection";
import FaqMessagesConfigSection from "./FaqMessagesConfigSection";
import FaqRetrievalConfigSection from "./FaqRetrievalConfigSection";
import PassagemRecoveryConfigSection from "./PassagemRecoveryConfigSection";
import TicketPresellerConfigSection from "./TicketPresellerConfigSection";
import SdrSkillConfigSection from "./SdrSkillConfigSection";
import SocialMediaSkillConfigSection from "./SocialMediaSkillConfigSection";
import ProductSellerSkillConfigSection from "./ProductSellerSkillConfigSection";
import ComplaintsSuggestionsSkillConfigSection from "./ComplaintsSuggestionsSkillConfigSection";
import OpsIntelligenceConfigSection from "./OpsIntelligenceConfigSection";
import SkillLifecycleRules from "./SkillLifecycleRules";
import { getPaymentMethodTipoLabel } from "../../constants/paymentMethodTipo";
import { CHANNEL_TYPES, isActiveChannel } from "../../constants/channelCatalog";
import { translateLifecycle, translateSkillForm } from "../../utils/skillLifecycle";
import { useUserAgendaCalendars } from "../../hooks/useUserAgenda";
import { useChannels } from "../../hooks/useChannels";

function ConfigAccordion({ title, subtitle, defaultExpanded = false, children }) {
  return (
    <Accordion
      variant="outlined"
      disableGutters
      defaultExpanded={defaultExpanded}
      sx={{
        "&:before": { display: "none" },
        borderRadius: "12px !important",
        overflow: "hidden",
        borderColor: "divider",
        "&.Mui-expanded": { mb: 0 },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ minHeight: 48, "& .MuiAccordionSummary-content": { my: 1 } }}
      >
        <Box sx={{ pr: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25, lineHeight: 1.4 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, pb: 2 }}>{children}</AccordionDetails>
    </Accordion>
  );
}

/**
 * Parâmetros da skill selecionada, com acordeões por tema (comportamento, horário, encerramento).
 */
export default function AgentSkillConfigPanel({
  primarySkillKey,
  isPdfTemplateSkill,
  canConfigureTicketSeller,
  readOnly,
  mergeSkillConfig,
  faqCfg,
  ticketSellerCfg,
  proposalCfg,
  contractCfg,
  skills,
  currentAgentId,
  canViewPaymentMethods,
  paymentMethodsLoading,
  paymentMethodsError,
  paymentMethodsForSelect,
  selectedFormaIdStr,
  orphanFormaRow,
  knowledgeDocuments = [],
  knowledgeSourceUrls = [],
}) {
  const { t } = useTranslation("settings");
  const isAppointmentScheduler = primarySkillKey === "appointment_scheduler";
  const isSdr = primarySkillKey === "sdr";
  const isSocialMedia = primarySkillKey === "social_media";
  const isProductSeller = primarySkillKey === "product_seller";
  const isComplaintsSuggestions = primarySkillKey === "complaints_suggestions";
  const isOpsIntelligence = primarySkillKey === "ops_intelligence";
  const isPassagemRecovery = primarySkillKey === "passagem_recovery";
  const isTicketPreseller = primarySkillKey === "ticket_preseller";
  const {
    data: agendaCalendarsData,
    isLoading: agendaCalendarsLoading,
    isError: agendaCalendarsError,
  } = useUserAgendaCalendars({ enabled: isAppointmentScheduler });
  const agendaCalendars = Array.isArray(agendaCalendarsData?.calendars)
    ? agendaCalendarsData.calendars
    : [];

  const formCopy = (suffix, fallbackKey) => translateSkillForm(t, primarySkillKey, suffix, fallbackKey);
  const lifecycleCopy = (suffix, fallbackKey) => translateLifecycle(t, primarySkillKey, suffix, fallbackKey);

  const skillConfig =
    primarySkillKey === "faq"
      ? faqCfg
      : primarySkillKey === "ticket_seller"
        ? ticketSellerCfg
        : primarySkillKey === "proposal_generator"
          ? proposalCfg
          : primarySkillKey === "contract_generator"
          ? contractCfg
          : skills[0]?.config || {};

  const { data: channelsData, isLoading: emailChannelsLoading } = useChannels(
    1,
    500,
    "",
  );
  const emailChannels = useMemo(() => {
    const rows = Array.isArray(channelsData?.channels) ? channelsData.channels : [];
    return rows.filter(
      (c) => Number(c?.tipo) === CHANNEL_TYPES.EMAIL_AWS_SES && isActiveChannel(c)
    );
  }, [channelsData]);
  const hasEmailChannel = emailChannels.length > 0;
  const selectedEmailChannelId =
    ticketSellerCfg?.id_canal_email_bpe != null && String(ticketSellerCfg.id_canal_email_bpe).trim() !== ""
      ? String(ticketSellerCfg.id_canal_email_bpe)
      : "";

  useEffect(() => {
    if (primarySkillKey !== "ticket_seller" || !canConfigureTicketSeller || readOnly) return;
    if (emailChannelsLoading) return;

    if (!hasEmailChannel) {
      if (ticketSellerCfg?.require_email_for_bpe !== false || selectedEmailChannelId) {
        mergeSkillConfig("ticket_seller", {
          require_email_for_bpe: false,
          id_canal_email_bpe: "",
        });
      }
      return;
    }

    if (ticketSellerCfg?.require_email_for_bpe === false) return;

    if (emailChannels.length === 1) {
      const onlyId = String(emailChannels[0].id);
      if (selectedEmailChannelId !== onlyId) {
        mergeSkillConfig("ticket_seller", { id_canal_email_bpe: onlyId });
      }
      return;
    }

    if (
      selectedEmailChannelId &&
      !emailChannels.some((c) => String(c.id) === selectedEmailChannelId)
    ) {
      mergeSkillConfig("ticket_seller", { id_canal_email_bpe: "" });
    }
  }, [
    primarySkillKey,
    canConfigureTicketSeller,
    readOnly,
    emailChannelsLoading,
    hasEmailChannel,
    emailChannels,
    selectedEmailChannelId,
    ticketSellerCfg?.require_email_for_bpe,
    mergeSkillConfig,
  ]);

  return (
    <Stack spacing={1}>
      {primarySkillKey === "proposal_generator" && (
        <Alert severity="info" sx={{ py: 0.75 }}>
          {t("agentsListPage.skillParamsProposalSetupHint")}
        </Alert>
      )}
      {primarySkillKey === "contract_generator" && (
        <Alert severity="info" sx={{ py: 0.75 }}>
          {t("agentsListPage.skillParamsContractSetupHint")}
        </Alert>
      )}

      {primarySkillKey === "receptionist" && (
        <ConfigAccordion
          title={t("agentsListPage.skillParamsReceptionistTitle")}
          subtitle={t("agentsListPage.skillParamsReceptionistSubtitle")}
          defaultExpanded
        >
          <Stack spacing={1.75}>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
              {t("agentsListPage.skillParamsReceptionistIntro")}
            </Typography>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {t("agentsListPage.skillParamsReceptionistCatalogTitle")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                {t("agentsListPage.skillParamsReceptionistCatalogBody")}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {t("agentsListPage.skillParamsReceptionistPositionsTitle")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                {t("agentsListPage.skillParamsReceptionistPositionsBody")}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {t("agentsListPage.skillParamsReceptionistContextTitle")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                {t("agentsListPage.skillParamsReceptionistContextBody")}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {t("agentsListPage.skillParamsReceptionistAiTitle")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                {t("agentsListPage.skillParamsReceptionistAiBody")}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {t("agentsListPage.skillParamsReceptionistLifecycleTitle")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                {t("agentsListPage.skillParamsReceptionistLifecycleBody")}
              </Typography>
            </Box>

            <Alert severity="info" sx={{ py: 0.75 }}>
              {t("agentsListPage.skillParamsReceptionistImpactNote")}
            </Alert>
            <Alert severity="info" sx={{ py: 0.75 }}>
              {t("agentsListPage.skillParamsReceptionistSimulatorNote")}
            </Alert>
          </Stack>
        </ConfigAccordion>
      )}

      {primarySkillKey === "ticket_seller" && canConfigureTicketSeller && (
        <ConfigAccordion
          title={t("agentsListPage.skillParamsPassengerTitle")}
          subtitle={formCopy("accordionTicketSubtitle", "agentsListPage.formSkillAccordionTicketHint")}
          defaultExpanded
        >
          <Stack spacing={2}>
            {!hasEmailChannel && !emailChannelsLoading ? (
              <Alert severity="info" sx={{ py: 0.75 }}>
                {t("agentsListPage.skillParamsRequireEmailBpeNoChannel")}{" "}
                <Link component={RouterLink} to="/settings/channels" underline="hover">
                  {t("agentsListPage.skillParamsRequireEmailBpeChannelsLink")}
                </Link>
              </Alert>
            ) : (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={hasEmailChannel && ticketSellerCfg.require_email_for_bpe !== false}
                      onChange={(e) => {
                        const on = e.target.checked;
                        const patch = { require_email_for_bpe: on };
                        if (!on) {
                          patch.id_canal_email_bpe = "";
                        } else if (emailChannels.length === 1) {
                          patch.id_canal_email_bpe = String(emailChannels[0].id);
                        }
                        mergeSkillConfig("ticket_seller", patch);
                      }}
                      disabled={readOnly || emailChannelsLoading || !hasEmailChannel}
                    />
                  }
                  label={t("agentsListPage.skillParamsRequireEmailBpe")}
                />
                {hasEmailChannel && ticketSellerCfg.require_email_for_bpe !== false && emailChannels.length > 1 && (
                  <FormControl fullWidth size="small" disabled={readOnly || emailChannelsLoading}>
                    <InputLabel id="agent-ticket-email-channel-label">
                      {t("agentsListPage.skillParamsBpeEmailChannel")}
                    </InputLabel>
                    <Select
                      labelId="agent-ticket-email-channel-label"
                      label={t("agentsListPage.skillParamsBpeEmailChannel")}
                      value={selectedEmailChannelId}
                      onChange={(e) =>
                        mergeSkillConfig("ticket_seller", {
                          id_canal_email_bpe: e.target.value === "" ? "" : String(e.target.value),
                        })
                      }
                    >
                      <MenuItem value="">
                        <em>{t("agentsListPage.skillParamsBpeEmailChannelEmpty")}</em>
                      </MenuItem>
                      {emailChannels.map((ch) => (
                        <MenuItem key={ch.id} value={String(ch.id)}>
                          {ch.nome || ch.email || `Canal ${ch.id}`}
                          {ch.email ? ` (${ch.email})` : ""}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                      {t("agentsListPage.skillParamsBpeEmailChannelHelper")}
                    </Typography>
                  </FormControl>
                )}
                {hasEmailChannel &&
                  ticketSellerCfg.require_email_for_bpe !== false &&
                  emailChannels.length > 1 &&
                  !selectedEmailChannelId && (
                    <Alert severity="warning" sx={{ py: 0.5 }}>
                      {t("agentsListPage.skillParamsBpeEmailChannelRequired")}
                    </Alert>
                  )}
              </>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={ticketSellerCfg.offer_whatsapp_flow === true}
                  onChange={(e) =>
                    mergeSkillConfig("ticket_seller", { offer_whatsapp_flow: e.target.checked })
                  }
                  disabled={readOnly}
                />
              }
              label={t("agentsListPage.skillParamsOfferWhatsappFlow")}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1, display: "block" }}>
              {t("agentsListPage.skillParamsOfferWhatsappFlowHelper")}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: -0.5, display: "block" }}>
              {t("agentsListPage.skillParamsOfferWhatsappFlowGratuitiesHint")}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={ticketSellerCfg.require_passenger_birth_date === true}
                  onChange={(e) =>
                    mergeSkillConfig("ticket_seller", {
                      require_passenger_birth_date: e.target.checked,
                    })
                  }
                  disabled={readOnly}
                />
              }
              label={t("agentsListPage.skillParamsRequirePassengerBirthDate")}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1, display: "block" }}>
              {t("agentsListPage.skillParamsRequirePassengerBirthDateHelper")}
            </Typography>
            {canViewPaymentMethods && !paymentMethodsError ? (
              <FormControl fullWidth size="small" disabled={readOnly || paymentMethodsLoading}>
                <InputLabel id="agent-ticket-forma-pagamento-label">
                  {t("agentsListPage.skillParamsIdFormaPagamento")}
                </InputLabel>
                <Select
                  labelId="agent-ticket-forma-pagamento-label"
                  label={t("agentsListPage.skillParamsIdFormaPagamento")}
                  value={selectedFormaIdStr}
                  onChange={(e) => {
                    const v = e.target.value;
                    mergeSkillConfig("ticket_seller", {
                      id_formadepagamento: v === "" ? "" : String(v),
                    });
                  }}
                >
                  <MenuItem value="">
                    <em>{t("agentsListPage.skillParamsFormaPagamentoDefault")}</em>
                  </MenuItem>
                  {paymentMethodsForSelect.map((m) => (
                    <MenuItem key={m.id} value={String(m.id)}>
                      {m.nome || "—"} — {getPaymentMethodTipoLabel(m.tipo)} (ID {m.id})
                    </MenuItem>
                  ))}
                  {selectedFormaIdStr &&
                    !paymentMethodsForSelect.some((m) => String(m.id) === selectedFormaIdStr) && (
                      <MenuItem value={selectedFormaIdStr}>
                        {orphanFormaRow
                          ? `${orphanFormaRow.nome || "—"} — ${getPaymentMethodTipoLabel(orphanFormaRow.tipo)} (ID ${orphanFormaRow.id}, ${t("agentsListPage.skillParamsFormaPagamentoInactive")})`
                          : t("agentsListPage.skillParamsFormaPagamentoUnknownId", { id: selectedFormaIdStr })}
                      </MenuItem>
                    )}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                  {t("agentsListPage.skillParamsIdFormaPagamentoHelper")}
                </Typography>
                {paymentMethodsLoading && (
                  <Typography variant="caption" color="text.secondary">
                    {t("agentsListPage.skillParamsFormaPagamentoLoading")}
                  </Typography>
                )}
              </FormControl>
            ) : (
              <>
                {!canViewPaymentMethods && (
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    {t("agentsListPage.skillParamsFormaPagamentoNoListPermission")}
                  </Alert>
                )}
                {paymentMethodsError && canViewPaymentMethods && (
                  <Alert severity="warning" sx={{ py: 0.5 }}>
                    {t("agentsListPage.skillParamsFormaPagamentoLoadError")}
                  </Alert>
                )}
                <TextField
                  label={t("agentsListPage.skillParamsIdFormaPagamentoManual")}
                  helperText={t("agentsListPage.skillParamsIdFormaPagamentoHelper")}
                  value={
                    ticketSellerCfg.id_formadepagamento != null
                      ? String(ticketSellerCfg.id_formadepagamento)
                      : ""
                  }
                  onChange={(e) =>
                    mergeSkillConfig("ticket_seller", {
                      id_formadepagamento: e.target.value.trim() === "" ? "" : e.target.value.trim(),
                    })
                  }
                  fullWidth
                  size="small"
                  disabled={readOnly}
                />
              </>
            )}
            <TextField
              select
              label={t("agentsListPage.skillParamsTripDisplay")}
              helperText={t("agentsListPage.skillParamsTripDisplayHelper")}
              value={ticketSellerCfg.trip_display === "carousel" ? "carousel" : "cards"}
              onChange={(e) => mergeSkillConfig("ticket_seller", { trip_display: e.target.value })}
              fullWidth
              size="small"
              disabled={readOnly}
            >
              <MenuItem value="cards">{t("agentsListPage.skillParamsTripDisplayCards")}</MenuItem>
              <MenuItem value="carousel">{t("agentsListPage.skillParamsTripDisplayCarousel")}</MenuItem>
            </TextField>
            <TextField
              label={t("agentsListPage.skillParamsBpeFlowNote")}
              value={ticketSellerCfg.bpe_flow_note || ""}
              onChange={(e) => mergeSkillConfig("ticket_seller", { bpe_flow_note: e.target.value })}
              multiline
              minRows={2}
              fullWidth
              size="small"
              disabled={readOnly}
            />
            <TextField
              label={t("agentsListPage.formSkillSalesAnswerFocus")}
              helperText={t("agentsListPage.formSkillSalesAnswerFocusHelper")}
              value={ticketSellerCfg.sales_answer_focus || ""}
              onChange={(e) =>
                mergeSkillConfig("ticket_seller", { sales_answer_focus: e.target.value })
              }
              multiline
              minRows={2}
              fullWidth
              size="small"
              disabled={readOnly}
            />
          </Stack>
        </ConfigAccordion>
      )}

      {primarySkillKey === "faq" && (
        <>
          <ConfigAccordion
            title={formCopy("accordionFaqBehaviorTitle", "agentsListPage.formSkillAccordionFaqBehavior")}
            subtitle={formCopy("accordionFaqBehaviorSubtitle", "agentsListPage.formSkillAccordionFaqBehaviorHint")}
            defaultExpanded
          >
            <TextField
              label={t("agentsListPage.skillParamsFaqFocus")}
              helperText={formCopy("faqFocusHelper", "agentsListPage.skillParamsFaqFocusHelper")}
              value={faqCfg.faq_answer_focus || ""}
              onChange={(e) => mergeSkillConfig("faq", { faq_answer_focus: e.target.value })}
              multiline
              minRows={3}
              fullWidth
              size="small"
              disabled={readOnly}
            />
          </ConfigAccordion>
          <ConfigAccordion
            title={t("agentsListPage.faqRetrievalTitle")}
            subtitle={t("agentsListPage.faqRetrievalSubtitle")}
            defaultExpanded
          >
            <FaqRetrievalConfigSection
              skillConfig={faqCfg}
              disabled={readOnly}
              onChange={(partial) => mergeSkillConfig("faq", partial)}
              documents={knowledgeDocuments}
              sourceUrls={knowledgeSourceUrls}
            />
          </ConfigAccordion>
          <ConfigAccordion
            title={t("agentsListPage.faqMessagesTitle")}
            subtitle={formCopy("accordionFaqMessagesSubtitle", "agentsListPage.faqMessagesSubtitle")}
          >
            <FaqMessagesConfigSection
              skillConfig={faqCfg}
              disabled={readOnly}
              onChange={(partial) => mergeSkillConfig("faq", partial)}
            />
          </ConfigAccordion>
          <ConfigAccordion
            title={t("agentsListPage.faqBusinessHoursTitle")}
            subtitle={formCopy("accordionHoursSubtitle", "agentsListPage.formSkillAccordionHoursHint")}
          >
            <FaqBusinessHoursSection
              skillConfig={faqCfg}
              disabled={readOnly}
              onChange={(partial) => mergeSkillConfig("faq", partial)}
            />
          </ConfigAccordion>
        </>
      )}

      {isAppointmentScheduler && (
        <ConfigAccordion
          title={t("agentsListPage.skillParamsAppointmentTitle")}
          subtitle={formCopy("accordionAppointmentSubtitle", "agentsListPage.formSkillAccordionAppointmentHint")}
          defaultExpanded
        >
          <Stack spacing={2}>
            <TextField
              label={t("agentsListPage.skillParamsAppointmentObjective")}
              helperText={t("agentsListPage.skillParamsAppointmentObjectiveHelper")}
              value={skillConfig.appointment_objective || ""}
              onChange={(e) =>
                mergeSkillConfig("appointment_scheduler", { appointment_objective: e.target.value })
              }
              placeholder={t("agentsListPage.skillParamsAppointmentObjectivePlaceholder")}
              fullWidth
              size="small"
              disabled={readOnly}
            />
            <FormControl fullWidth size="small" disabled={readOnly || agendaCalendarsLoading}>
              <InputLabel id="agent-appointment-calendar-label">
                {t("agentsListPage.skillParamsAppointmentCalendar")}
              </InputLabel>
              <Select
                labelId="agent-appointment-calendar-label"
                label={t("agentsListPage.skillParamsAppointmentCalendar")}
                value={skillConfig.calendar_id ? String(skillConfig.calendar_id) : ""}
                onChange={(e) => {
                  const value = e.target.value;
                  const calendar = agendaCalendars.find((cal) => String(cal.id) === String(value));
                  mergeSkillConfig("appointment_scheduler", {
                    calendar_id: value ? String(value) : "",
                    calendar_name: calendar?.name || "",
                    organizer_user_id: calendar?.ownerUserId ? String(calendar.ownerUserId) : "",
                  });
                }}
              >
                <MenuItem value="">
                  <em>{t("agentsListPage.skillParamsAppointmentCalendarEmpty")}</em>
                </MenuItem>
                {agendaCalendars
                  .filter((cal) => cal.canCreate || cal.canManage)
                  .map((cal) => (
                    <MenuItem key={cal.id} value={String(cal.id)}>
                      {cal.name} {cal.type ? `(${cal.type})` : ""}
                    </MenuItem>
                  ))}
                {skillConfig.calendar_id &&
                  !agendaCalendars.some((cal) => String(cal.id) === String(skillConfig.calendar_id)) && (
                    <MenuItem value={String(skillConfig.calendar_id)}>
                      {skillConfig.calendar_name ||
                        t("agentsListPage.skillParamsAppointmentCalendarUnknown", {
                          id: skillConfig.calendar_id,
                        })}
                    </MenuItem>
                  )}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                {agendaCalendarsLoading
                  ? t("agentsListPage.skillParamsAppointmentCalendarLoading")
                  : t("agentsListPage.skillParamsAppointmentCalendarHelper")}
              </Typography>
            </FormControl>
            {agendaCalendarsError && (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                {t("agentsListPage.skillParamsAppointmentCalendarLoadError")}
              </Alert>
            )}
            <TextField
              label={t("agentsListPage.skillParamsAppointmentDuration")}
              helperText={t("agentsListPage.skillParamsAppointmentDurationHelper")}
              value={skillConfig.default_duration_minutes || ""}
              onChange={(e) =>
                mergeSkillConfig("appointment_scheduler", {
                  default_duration_minutes: e.target.value.replace(/\D/g, ""),
                })
              }
              placeholder="60"
              fullWidth
              size="small"
              disabled={readOnly}
            />
            <TextField
              label={t("agentsListPage.skillParamsAppointmentNote")}
              helperText={t("agentsListPage.skillParamsAppointmentNoteHelper")}
              value={skillConfig.scheduling_note || ""}
              onChange={(e) => mergeSkillConfig("appointment_scheduler", { scheduling_note: e.target.value })}
              multiline
              minRows={2}
              fullWidth
              size="small"
              disabled={readOnly}
            />
          </Stack>
        </ConfigAccordion>
      )}

      {isSdr && (
        <SdrSkillConfigSection
          skillConfig={skillConfig}
          mergeSkillConfig={mergeSkillConfig}
          readOnly={readOnly}
        />
      )}

      {isSocialMedia && (
        <SocialMediaSkillConfigSection
          skillConfig={skillConfig}
          mergeSkillConfig={mergeSkillConfig}
          readOnly={readOnly}
          currentAgentId={currentAgentId}
        />
      )}

      {isProductSeller && (
        <ProductSellerSkillConfigSection
          skillConfig={skillConfig}
          mergeSkillConfig={mergeSkillConfig}
          readOnly={readOnly}
          canViewPaymentMethods={canViewPaymentMethods}
          paymentMethodsLoading={paymentMethodsLoading}
          paymentMethodsForSelect={paymentMethodsForSelect}
          selectedFormaIdStr={selectedFormaIdStr}
          orphanFormaRow={orphanFormaRow}
        />
      )}


      {isPassagemRecovery && canConfigureTicketSeller && (
        <PassagemRecoveryConfigSection
          skillConfig={skillConfig}
          mergeRecoveryConfig={(partial) => mergeSkillConfig("passagem_recovery", partial)}
          readOnly={readOnly}
          currentAgentId={currentAgentId}
        />
      )}

      {isPassagemRecovery && !canConfigureTicketSeller && (
        <Alert severity="warning">{t("agentsListPage.recoverySegmentOnly")}</Alert>
      )}

      {isTicketPreseller && canConfigureTicketSeller && (
        <TicketPresellerConfigSection
          skillConfig={skillConfig}
          mergePresellerConfig={(partial) => mergeSkillConfig("ticket_preseller", partial)}
          readOnly={readOnly}
          currentAgentId={currentAgentId}
        />
      )}

      {isTicketPreseller && !canConfigureTicketSeller && (
        <Alert severity="warning">{t("agentsListPage.presellerSegmentOnly")}</Alert>
      )}

      {isComplaintsSuggestions && (
        <ComplaintsSuggestionsSkillConfigSection
          skillConfig={skillConfig}
          mergeComplaintsConfig={(partial) => mergeSkillConfig("complaints_suggestions", partial)}
          readOnly={readOnly}
          currentAgentId={currentAgentId}
        />
      )}

      {isOpsIntelligence && (
        <OpsIntelligenceConfigSection
          skillConfig={skillConfig}
          mergeOpsConfig={(partial) => mergeSkillConfig("ops_intelligence", partial)}
          readOnly={readOnly}
          currentAgentId={currentAgentId}
        />
      )}

      {!isPdfTemplateSkill &&
        primarySkillKey &&
        primarySkillKey !== "faq" &&
        primarySkillKey !== "ticket_seller" &&
        primarySkillKey !== "ticket_preseller" &&
        primarySkillKey !== "passagem_recovery" &&
        primarySkillKey !== "complaints_suggestions" &&
        primarySkillKey !== "ops_intelligence" &&
        primarySkillKey !== "appointment_scheduler" &&
        primarySkillKey !== "sdr" &&
        primarySkillKey !== "social_media" && (
          <Alert severity="info" sx={{ py: 0.75 }}>
            {formCopy("knowledgeNotForThisSkill", "agentsListPage.knowledgeNotForThisSkill")}
          </Alert>
        )}

      {!isOpsIntelligence ? (
      <ConfigAccordion
        title={lifecycleCopy("sectionTitle", "agentsListPage.lifecycleSectionTitle")}
        subtitle={formCopy(
          "accordionLifecycleSubtitle",
          "agentsListPage.formSkillAccordionLifecycleHint"
        )}
        defaultExpanded={!!isPdfTemplateSkill}
      >
        <SkillLifecycleRules
          skillKey={primarySkillKey}
          skillConfig={skillConfig}
          currentAgentId={currentAgentId}
          disabled={readOnly}
          embedded
          onChange={(partial) => mergeSkillConfig(primarySkillKey, partial)}
        />
      </ConfigAccordion>
      ) : null}
    </Stack>
  );
}
